import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Card, Table, Form, Button, Spinner, Badge, InputGroup, Modal,
} from "react-bootstrap";
import Pagination from "../components/Pagination";
import moment from "moment";
import { getVoiceConversations } from "../context/functions/AdminFunction";
import {
  FiPhone, FiPhoneOff, FiSearch, FiX, FiRefreshCw, FiClock,
  FiUser, FiMail, FiChevronUp, FiChevronDown, FiInfo,
} from "react-icons/fi";
import "../styles/llm-cost-analysis.css";

const SYNC_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "done", label: "Done" },
  { value: "failed", label: "Failed" },
  { value: "in-progress", label: "In Progress" },
  { value: "processing", label: "Processing" },
  { value: "initiated", label: "Initiated" },
];

const SYNC_STATUS_KEYS = ["done", "failed", "in-progress", "processing", "initiated"];

const CALL_STATUS_OPTIONS = [
  { value: "", label: "All Statuses" },
  { value: "success", label: "Success" },
  { value: "failure", label: "Failure" },
  { value: "unknown", label: "Unknown" },
];

const CALL_STATUS_KEYS = ["success", "failure", "unknown"];

const STATUS_BADGE = {
  done: { bg: "success", label: "Done", color: "#22c55e" },
  failed: { bg: "danger", label: "Failed", color: "#ef4444" },
  "in-progress": { bg: "warning", label: "In Progress", color: "#f59e0b" },
  processing: { bg: "info", label: "Processing", color: "#3b82f6" },
  initiated: { bg: "secondary", label: "Initiated", color: "#64748b" },
  success: { bg: "success", label: "Success", color: "#22c55e" },
  failure: { bg: "danger", label: "Failure", color: "#ef4444" },
  unknown: { bg: "secondary", label: "Unknown", color: "#94a3b8" },
};

// Path within collected_data where the ElevenLabs "call_verdict" is stored
const getCallVerdict = (conv) =>
  conv?.collected_data?.analysis?.data_collection_results?.call_verdict?.value ||
  null;

const formatVerdictLabel = (v) =>
  String(v)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (l) => l.toUpperCase());

const normalizeAgentOptions = (options = []) => {
  const uniqueAgents = new Map();

  options.forEach((option) => {
    const value = typeof option === "string"
      ? option
      : option?.value || option?.agent_id || option?.id;
    const label = typeof option === "string"
      ? option
      : option?.label || option?.agent_name || option?.name || value;

    if (value) uniqueAgents.set(String(value), String(label || value));
  });

  return Array.from(uniqueAgents, ([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

const formatDuration = (seconds) => {
  if (!seconds && seconds !== 0) return "—";
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m > 0 ? `${m}m ${s}s` : `${s}s`;
};

const StatCard = ({ icon, label, value, sub, color }) => (
  <div
    style={{
      background: "#fff",
      border: "1px solid #e8edf2",
      borderRadius: "12px",
      padding: "18px 20px",
      display: "flex",
      alignItems: "center",
      gap: "14px",
      flex: 1,
      minWidth: 0,
    }}
  >
    <div
      style={{
        width: 42,
        height: 42,
        borderRadius: "10px",
        background: color || "rgba(163,235,101,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#253837",
        fontSize: 18,
        flexShrink: 0,
      }}
    >
      {icon}
    </div>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 2 }}>
        {label}
      </div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "#253837", lineHeight: 1.2 }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: "#94a3b8", fontWeight: 600, marginTop: 2 }}>{sub}</div>}
    </div>
  </div>
);

const SortHeader = ({ label, field, sortBy, sortOrder, onSort }) => {
  const active = sortBy === field;
  return (
    <th
      onClick={() => onSort(field)}
      style={{ cursor: "pointer", userSelect: "none", whiteSpace: "nowrap" }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
        {label}
        {active ? (
          sortOrder === "desc" ? <FiChevronDown size={12} /> : <FiChevronUp size={12} />
        ) : (
          <FiChevronDown size={12} style={{ opacity: 0.3 }} />
        )}
      </span>
    </th>
  );
};

const VoiceConversations = () => {
  const [conversations, setConversations] = useState([]);
  const [stats, setStats] = useState(null);
  const [verdictOptions, setVerdictOptions] = useState([]);
  const [agentOptions, setAgentOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const limit = 20;

  // Filters
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [syncStatus, setSyncStatus] = useState("");
  const [callStatus, setCallStatus] = useState("");
  const [callVerdict, setCallVerdict] = useState("");
  const [agentId, setAgentId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [sortBy, setSortBy] = useState("started_at");
  const [sortOrder, setSortOrder] = useState("desc");

  // Detail Modal
  const [selectedConv, setSelectedConv] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const abortControllerRef = useRef(null);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    document.title = "11Labs Conversations | Admin";
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const fetchData = useCallback(async (currentPage = 1, signal = null) => {
    setLoading(true);
    try {
      const filters = {
        search: debouncedSearch,
        call_status: syncStatus,
        call_successful: callStatus,
        call_verdict: callVerdict,
        agent_id: agentId,
        startDate,
        endDate,
        sortBy,
        sortOrder,
      };
      const res = await getVoiceConversations(filters, currentPage, limit, signal);
      if (res?.status === "canceled") {
        return;
      }
      if (res && res.success) {
        setConversations(res.data.conversations || []);
        setStats(res.data.stats || null);
        if (Array.isArray(res.data.verdictOptions)) {
          setVerdictOptions(res.data.verdictOptions);
        }
        const responseAgentOptions = res.data.agentOptions || res.data.agents;
        const returnedAgents = (res.data.conversations || []).map((conversation) => ({
          agent_id: conversation.agent_id,
          agent_name: conversation.agent_name,
        }));
        const normalizedAgents = normalizeAgentOptions(
          Array.isArray(responseAgentOptions) ? responseAgentOptions : returnedAgents
        );
        setAgentOptions((currentOptions) => normalizeAgentOptions([...currentOptions, ...normalizedAgents]));
        setTotalPages(res.data.pagination?.totalPages || 1);
        setTotalRecords(res.data.pagination?.totalRecords || 0);
      } else {
        setConversations([]);
        setStats(null);
        setTotalPages(1);
        setTotalRecords(0);
      }
    } catch (e) {
      console.error(e);
      setConversations([]);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, syncStatus, callStatus, callVerdict, agentId, startDate, endDate, sortBy, sortOrder]);

  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    fetchData(page, controller.signal);

    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [fetchData, page]);

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((o) => (o === "desc" ? "asc" : "desc"));
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
    setPage(1);
  };

  const handlePageClick = (e) => setPage(e.selected + 1);

  const handleReset = () => {
    setSearch("");
    setSyncStatus("");
    setCallStatus("");
    setCallVerdict("");
    setAgentId("");
    setStartDate("");
    setEndDate("");
    setSortBy("started_at");
    setSortOrder("desc");
    setPage(1);
  };

  const handleOpenModal = (conv) => {
    setSelectedConv(conv);
    setShowModal(true);
  };

  const getStatusBadge = (status) => {
    const cfg = STATUS_BADGE[status] || { bg: "secondary", label: status || "Unknown" };
    return <Badge bg={cfg.bg} style={{ fontSize: "11px", fontWeight: 700 }}>{cfg.label}</Badge>;
  };

  // ---- Stats bar ----
  const statusBreakdown = stats?.statusBreakdown || {};
  const doneCount = (statusBreakdown["done"] || 0) + (statusBreakdown["success"] || 0);
  const failedCount = (statusBreakdown["failed"] || 0) + (statusBreakdown["failure"] || 0);

  return (
    <div className="llm-cost-container">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-800 mb-1" style={{ color: "var(--llm-color-text-primary)", fontSize: "22px" }}>
            11Labs Conversations
          </h4>
          <p className="text-muted small fw-medium mb-0">
            Voice conversation records from ElevenLabs agents.
          </p>
        </div>
        <Button
          variant="white"
          className="border shadow-none d-flex align-items-center gap-2"
          style={{ fontSize: "13px", fontWeight: 700, borderRadius: "8px" }}
          onClick={() => fetchData(page)}
        >
          <FiRefreshCw size={14} /> Refresh
        </Button>
      </div>      {/* Filters */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Body className="p-3">
          <div className="llm-filter-row" style={{ flexWrap: "wrap", gap: 10 }}>
            {/* Search */}
            <div className="llm-filter-item" style={{ flex: 2, minWidth: 200 }}>
              <span className="llm-filter-label">Search</span>
              <InputGroup size="sm">
                <InputGroup.Text className="bg-white border-end-0">
                  <FiSearch size={13} className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  className="border-start-0 ps-1"
                  style={{ height: "38px" }}
                  type="text"
                  placeholder="Name, phone, email, conversation ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search && (
                  <InputGroup.Text
                    className="bg-white border-start-0 cursor-pointer"
                    onClick={() => setSearch("")}
                    style={{ cursor: "pointer" }}
                  >
                    <FiX size={13} className="text-danger" />
                  </InputGroup.Text>
                )}
              </InputGroup>
            </div>

            {/* Agent */}
            <div className="llm-filter-item">
              <span className="llm-filter-label">Agent</span>
              <Form.Select
                size="sm"
                value={agentId}
                onChange={(e) => { setAgentId(e.target.value); setPage(1); }}
                style={{ height: "38px", borderRadius: "6px", minWidth: "160px" }}
              >
                <option value="">All Agents</option>
                {agentOptions.map((agent) => (
                  <option key={agent.value} value={agent.value}>{agent.label}</option>
                ))}
              </Form.Select>
            </div>

            {/* Status */}
            <div className="llm-filter-item">
              <span className="llm-filter-label">Status</span>
              <Form.Select
                size="sm"
                value={syncStatus}
                onChange={(e) => { setSyncStatus(e.target.value); setPage(1); }}
                style={{ height: "38px", borderRadius: "6px" }}
              >
                {SYNC_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>

            {/* Call Status */}
            <div className="llm-filter-item">
              <span className="llm-filter-label">Call Status</span>
              <Form.Select
                size="sm"
                value={callStatus}
                onChange={(e) => { setCallStatus(e.target.value); setPage(1); }}
                style={{ height: "38px", borderRadius: "6px" }}
              >
                {CALL_STATUS_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>

            {/* Call Verdict */}
            <div className="llm-filter-item">
              <span className="llm-filter-label">Call Verdict</span>
              <Form.Select
                size="sm"
                value={callVerdict}
                onChange={(e) => { setCallVerdict(e.target.value); setPage(1); }}
                style={{ height: "38px", borderRadius: "6px" }}
              >
                <option value="">All</option>
                <option value="__none__">No Verdict Required (—)</option>
                {verdictOptions.map((v) => (
                  <option key={v} value={v}>{formatVerdictLabel(v)}</option>
                ))}
              </Form.Select>
            </div>

            {/* Date From */}
            <div className="llm-filter-item">
              <span className="llm-filter-label">From</span>
              <Form.Control
                size="sm"
                type="date"
                value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                style={{ height: "38px", fontSize: "12px" }}
              />
            </div>

            {/* Date To */}
            <div className="llm-filter-item">
              <span className="llm-filter-label">To</span>
              <Form.Control
                size="sm"
                type="date"
                value={endDate}
                onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                style={{ height: "38px", fontSize: "12px" }}
              />
            </div>

            <Button
              className="llm-btn-apply"
              onClick={handleReset}
              style={{ background: "#f1f5f9", color: "#253837", border: "1px solid #e2e8f0" }}
            >
              Reset
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Breakdown Section */}
      {stats && (
        <div className="row mb-4">
          <div className="col-md-6">
            <Card className="llm-card shadow-sm h-100">
              <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-800 text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.05em" }}>
                  Call Status Breakdown
                </h6>
              </Card.Header>
              <div className="table-responsive">
                <table className="table table-borderless align-middle mb-0" style={{ fontSize: "13px" }}>
                  <thead style={{ background: "#f8fafc", borderTop: "1px solid #edf2f7" }}>
                    <tr>
                      <th className="ps-4 py-2 text-muted fw-bold text-uppercase" style={{ fontSize: "10px" }}>Call Status</th>
                      <th className="py-2 text-muted fw-bold text-uppercase text-center" style={{ fontSize: "10px" }}>Count</th>
                      <th className="pe-4 py-2 text-muted fw-bold text-uppercase" style={{ fontSize: "10px", width: "40%" }}>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CALL_STATUS_KEYS.map((key) => {
                      const count = stats.callSuccessfulBreakdown?.[key] || 0;
                      const share = stats.totalConversations > 0 ? ((count / stats.totalConversations) * 100).toFixed(1) : "0.0";
                      const barColor = STATUS_BADGE[key]?.color || "var(--llm-color-primary)";
                      return (
                        <tr key={key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td className="ps-4 py-3 fw-bold text-capitalize" style={{ color: "#475569" }}>{key}</td>
                          <td className="py-3 text-center fw-800" style={{ color: "#1e293b" }}>{count.toLocaleString()}</td>
                          <td className="pe-4 py-3">
                            <div className="d-flex align-items-center gap-3">
                              <div className="flex-grow-1" style={{ height: "6px", background: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${share}%`, background: barColor, borderRadius: "10px" }}></div>
                              </div>
                              <span className="fw-bold" style={{ fontSize: "11px", color: "#64748b", minWidth: "40px" }}>{share}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="mt-auto p-3 border-top" style={{ background: "#fcfcfc" }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>TOTAL CONVERSATIONS</span>
                  <span className="fw-800" style={{ fontSize: "14px", color: "#1e293b" }}>{stats.totalConversations.toLocaleString()}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <span className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>AVG. DURATION</span>
                  <span className="fw-800" style={{ fontSize: "14px", color: "#1e293b" }}>{formatDuration(stats.avgDurationSeconds)}</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <span className="text-muted" style={{ fontSize: "11px", fontWeight: 600 }}>TOTAL CREDITS</span>
                  <span className="fw-800" style={{ fontSize: "14px", color: "#1e293b" }}>{(stats.totalCostCredits || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}</span>
                </div>
              </div>
            </Card>
          </div>

          <div className="col-md-6">
            <Card className="llm-card shadow-sm h-100">
              <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
                <h6 className="mb-0 fw-800 text-uppercase" style={{ fontSize: "12px", letterSpacing: "0.05em" }}>
                  Status Breakdown
                </h6>
              </Card.Header>
              <div className="table-responsive">
                <table className="table table-borderless align-middle mb-0" style={{ fontSize: "13px" }}>
                  <thead style={{ background: "#f8fafc", borderTop: "1px solid #edf2f7" }}>
                    <tr>
                      <th className="ps-4 py-2 text-muted fw-bold text-uppercase" style={{ fontSize: "10px" }}>Status</th>
                      <th className="py-2 text-muted fw-bold text-uppercase text-center" style={{ fontSize: "10px" }}>Count</th>
                      <th className="pe-4 py-2 text-muted fw-bold text-uppercase" style={{ fontSize: "10px", width: "40%" }}>Share</th>
                    </tr>
                  </thead>
                  <tbody>
                    {SYNC_STATUS_KEYS.map((key) => {
                      const count = stats.statusBreakdown?.[key] || 0;
                      const share = stats.totalConversations > 0 ? ((count / stats.totalConversations) * 100).toFixed(1) : "0.0";
                      const barColor = STATUS_BADGE[key]?.color || "var(--llm-color-primary)";
                      return (
                        <tr key={key} style={{ borderBottom: "1px solid #f1f5f9" }}>
                          <td className="ps-4 py-3 fw-bold text-capitalize" style={{ color: "#475569" }}>{key.replace("-", " ")}</td>
                          <td className="py-3 text-center fw-800" style={{ color: "#1e293b" }}>{count.toLocaleString()}</td>
                          <td className="pe-4 py-3">
                            <div className="d-flex align-items-center gap-3">
                              <div className="flex-grow-1" style={{ height: "6px", background: "#f1f5f9", borderRadius: "10px", overflow: "hidden" }}>
                                <div style={{ height: "100%", width: `${share}%`, background: barColor, borderRadius: "10px" }}></div>
                              </div>
                              <span className="fw-bold" style={{ fontSize: "11px", color: "#64748b", minWidth: "40px" }}>{share}%</span>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </Card>
          </div>

        </div>
      )}

      {/* Conversations Table */}
      <Card className="llm-card shadow-sm mb-4">
        <Card.Header className="bg-white border-0 py-3 px-4 d-flex justify-content-between align-items-center">
          <h6 className="mb-0 fw-800 text-uppercase" style={{ fontSize: "13px", letterSpacing: "0.05em" }}>
            Conversation Records
          </h6>
          <span className="text-muted small fw-medium" style={{ fontSize: "11px" }}>
            {totalRecords.toLocaleString()} total records
          </span>
        </Card.Header>
        <div className="table-responsive">
          <Table className="llm-table" hover>
            <thead>
              <tr>
                <SortHeader label="Date / Time" field="started_at" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortHeader label="Customer" field="customer_name" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <th>Contact</th>
                <th>Agent</th>
                <th>Status</th>
                <th>Call Status</th>
                <th>Call Verdict</th>
                <SortHeader label="Duration" field="duration_seconds" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortHeader label="Credits" field="cost_credits" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <th className="text-center">Details</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="10" className="text-center py-5">
                    <Spinner size="sm" animation="border" variant="primary" />
                    <p className="mt-2 text-muted small fw-bold text-uppercase mb-0">Loading...</p>
                  </td>
                </tr>
              ) : conversations.length > 0 ? (
                conversations.map((conv) => (
                  <tr key={conv._id}>
                    <td>
                      <span className="fw-bold d-block text-dark" style={{ fontSize: "13px" }}>
                        {conv.started_at ? moment(conv.started_at).format("DD MMM YYYY") : "—"}
                      </span>
                      <small className="text-muted fw-medium" style={{ fontSize: "11px" }}>
                        {conv.started_at ? moment(conv.started_at).format("HH:mm:ss") : ""}
                      </small>
                    </td>
                    <td>
                      <div className="fw-bold" style={{ fontSize: "13px" }}>
                        {conv.customer_name || <span className="text-muted">Unknown</span>}
                      </div>
                    </td>
                    <td>
                      <div style={{ fontSize: "12px" }}>
                        {conv.contact_phone && (
                          <div className="text-muted d-flex align-items-center gap-1">
                            <FiPhone size={10} /> {conv.contact_phone}
                          </div>
                        )}
                        {conv.contact_email && (
                          <div className="text-muted d-flex align-items-center gap-1">
                            <FiMail size={10} /> {conv.contact_email}
                          </div>
                        )}
                        {!conv.contact_phone && !conv.contact_email && <span className="text-muted">—</span>}
                      </div>
                    </td>
                    <td>
                      <span style={{ fontSize: "12px", fontWeight: 600, color: "#475569" }}>
                        {conv.agent_name || conv.agent_id || "—"}
                      </span>
                    </td>
                    <td>{getStatusBadge(conv.call_status)}</td>
                    <td>{conv.call_successful ? getStatusBadge(conv.call_successful) : "—"}</td>
                    <td>
                      {(() => {
                        const verdict = getCallVerdict(conv);
                        if (!verdict) return <span className="text-muted">—</span>;
                        return (
                          <span
                            title={verdict}
                            style={{
                              display: "inline-block",
                              maxWidth: 180,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              verticalAlign: "middle",
                              fontSize: "12px",
                              fontWeight: 600,
                              color: "#475569",
                              background: "#f1f5f9",
                              border: "1px solid #e2e8f0",
                              borderRadius: "6px",
                              padding: "2px 8px",
                            }}
                          >
                            {formatVerdictLabel(verdict)}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="fw-600 text-muted">{formatDuration(conv.duration_seconds)}</td>
                    <td className="fw-600 text-muted">
                      {conv.cost_credits != null
                        ? Number(conv.cost_credits).toLocaleString(undefined, { maximumFractionDigits: 2 })
                        : "—"}
                    </td>
                    <td className="text-center">
                      <Button
                        variant="white"
                        className="border shadow-none py-1 px-2 d-inline-flex align-items-center gap-1"
                        style={{ fontSize: "11px", borderRadius: "6px", fontWeight: 700 }}
                        onClick={() => handleOpenModal(conv)}
                      >
                        <FiInfo size={12} /> View
                      </Button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="10" className="text-center py-5 text-muted">
                    <FiSearch size={32} className="mb-2 opacity-25 d-block mx-auto" />
                    <span className="fw-bold">No conversations found</span>
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        </div>

        {/* Pagination */}
        {!loading && conversations.length > 0 && (
          <div className="d-flex justify-content-end p-3 border-top bg-light bg-opacity-50">
            <Pagination
              currentPage={page - 1}
              totalPages={totalPages}
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              className="pagination mb-0"
            />
          </div>
        )}
      </Card>

      {/* Detail Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered className="audit-modal">
        <Modal.Header closeButton className="border-0 px-4 pt-4 pb-0">
          <Modal.Title className="fw-bold h5">Conversation Details</Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-4">
          {selectedConv && (
            <div>
              {/* Top info grid */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 20 }}>
                {[
                  { label: "Conversation ID", value: selectedConv.conversation_id || "—" },
                  { label: "Agent", value: selectedConv.agent_name || selectedConv.agent_id || "—" },
                  { label: "Status", value: getStatusBadge(selectedConv.call_status) },
                  { label: "Customer", value: selectedConv.customer_name || "Unknown" },
                  { label: "Phone", value: selectedConv.contact_phone || "—" },
                  { label: "Email", value: selectedConv.contact_email || "—" },
                  { label: "Started At", value: selectedConv.started_at ? moment(selectedConv.started_at).format("DD MMM YYYY, HH:mm:ss") : "—" },
                  { label: "Duration", value: formatDuration(selectedConv.duration_seconds) },
                  { label: "Credits Used", value: selectedConv.cost_credits != null ? Number(selectedConv.cost_credits).toLocaleString() : "—" },
                  { label: "Result", value: selectedConv.call_successful ? getStatusBadge(selectedConv.call_successful) : "—" },
                  { label: "LLM Credits", value: selectedConv.credits_llm != null ? Number(selectedConv.credits_llm).toLocaleString() : "—" },
                  { label: "LLM Cost (USD)", value: selectedConv.llm_cost_usd != null ? `$${Number(selectedConv.llm_cost_usd).toFixed(5)}` : "—" },
                  { label: "Stored At", value: selectedConv.created_at ? moment(selectedConv.created_at).format("DD MMM YYYY, HH:mm") : "—" },
                ].map(({ label, value }) => (
                  <div key={label} className="bg-light p-3 rounded-3 border">
                    <p className="text-muted small mb-1 fw-bold text-uppercase" style={{ fontSize: "10px" }}>{label}</p>
                    <div className="fw-bold text-dark" style={{ fontSize: "13px" }}>{value}</div>
                  </div>
                ))}
              </div>

              {/* Collected data — data_collection_results key/value */}
              {(() => {
                const dcr = selectedConv.collected_data?.analysis?.data_collection_results;
                if (!dcr || Object.keys(dcr).length === 0) return null;
                const entries = Object.entries(dcr);
                return (
                  <div>
                    <p className="text-muted small mb-2 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                      Collected Data
                    </p>
                    <div className="bg-light rounded-3 border" style={{ overflow: "hidden" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ background: "#f1f5f9" }}>
                            <th style={{ padding: "8px 14px", fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em", width: "40%" }}>Field</th>
                            <th style={{ padding: "8px 14px", fontSize: "11px", fontWeight: 700, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.04em" }}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {entries.map(([key, item], i) => (
                            <tr key={key} style={{ borderTop: "1px solid #e2e8f0", background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                              <td style={{ padding: "9px 14px", fontSize: "12px", fontWeight: 700, color: "#475569" }}>
                                {key.replace(/_/g, " ").replace(/\b\w/g, l => l.toUpperCase())}
                              </td>
                              <td style={{ padding: "9px 14px", fontSize: "13px", fontWeight: 600, color: item?.value != null ? "#1e293b" : "#94a3b8" }}>
                                {item?.value != null ? String(item.value) : <span style={{ fontStyle: "italic" }}>—</span>}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                );
              })()}

              {/* Summary Section */}
              {selectedConv.collected_data?.analysis?.transcript_summary && (
                <div className="mt-4">
                  <p className="text-muted small mb-2 fw-bold text-uppercase" style={{ fontSize: "11px" }}>
                    Transcript Summary
                  </p>
                  <div className="p-3 bg-light rounded-3 border" style={{ fontSize: "13px", lineHeight: "1.6", color: "#334155" }}>
                    {selectedConv.collected_data.analysis.transcript_summary}
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal.Body>
      </Modal>

      <style>{`
        .pagination .page-item .page-link {
          color: var(--llm-color-text-secondary);
          border: 1px solid var(--llm-color-border);
          padding: 6px 12px;
          font-size: 12px;
          font-weight: 600;
        }
        .pagination .page-item.active .page-link {
          background-color: var(--llm-color-primary) !important;
          border-color: var(--llm-color-primary) !important;
          color: var(--llm-color-dark) !important;
        }
        .pagination .page-item:not(.active) .page-link:hover {
          background-color: #f8fafc;
        }
      `}</style>
    </div>
  );
};

export default VoiceConversations;
